<script setup lang="ts">
import GridTripple from '~/shared/ui/grid/GridTripple.vue';
import MainNews from '~/entities/main-news/ui/MainNews.vue';
import { Subscribe } from '~/entities/subscribe/';
import { getOgImageMeta } from '~/shared/lib/seo';
import {
  isSpecialProjectExtraId,
  newsTypes,
  publicators,
  specialProjectExtras,
} from '../lib/const';
import {
  SEO_TITLE,
  SEO_DESCRIPTION,
  SEO_KEYWORDS,
  OG_TYPE,
  OG_TITLE,
  OG_DESCRIPTION,
} from '../lib/seo';
import {
  CLIENT_CHRONICLE_KEY,
  CLIENT_CHRONICLE_URL,
  CLIENT_CHRONICLE_PREVIEW_KEY,
  CLIENT_CHRONICLE_PREVIEW_URL,
} from '~/shared/api/client/publisher/publisher.const';
import type { IPublisherDataClientDTO } from '~/shared/api/client/publisher/publisher.dto';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { getRelatedPublisherItems } from '~/shared/lib/getRelatedPublisherItems';
import ChronicleBanner5 from '~/entities/chronicle_banners/ui/ChronicleBanner5.vue';
import { useOrigin } from '~/shared/lib/useOrigin';

type TComputedPublisherData = IPublisherDataClientDTO & {
  adjustedColSpan: number;
};

onUpdated(() => {
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
});

const origin = useOrigin();
const canonicalUrl = origin + '/';

useHead({
  title: SEO_TITLE,
  link: [{ rel: 'canonical', href: canonicalUrl }],
  meta: [
    { name: 'description', content: SEO_DESCRIPTION },
    { name: 'keywords', content: SEO_KEYWORDS },
    { property: 'og:type', content: OG_TYPE },
    { property: 'og:title', content: OG_TITLE },
    { property: 'og:keywords', content: SEO_KEYWORDS },
    { property: 'og:description', content: OG_DESCRIPTION },
    ...getOgImageMeta(origin),
  ],
  script: [
    {
      type: 'application/ld+json',
      key: 'ld-json-article',
      innerHTML: {},
    },
  ],
});

const route = useRoute();
const { $viewport } = useNuxtApp();

const columns = computed(() =>
  $viewport.isGreaterOrEquals('desktop')
    ? 3
    : $viewport.isGreaterOrEquals('tablet')
      ? 2
      : 1,
);

const layout = useState<IPublisherDataClientDTO[]>(
  'client_chronicle_layout',
  () => [],
);

const isPreview = computed(() => {
  return route.query?.hash;
});

const response = useCustomFetch<IPublisherDataClientDTO[]>(
  isPreview.value ? CLIENT_CHRONICLE_PREVIEW_URL : CLIENT_CHRONICLE_URL,
  {
    method: 'GET',
    key: isPreview.value ? CLIENT_CHRONICLE_PREVIEW_KEY : CLIENT_CHRONICLE_KEY,
    params: isPreview.value ? { hash: route.query?.hash } : undefined,
    onResponse({ response }) {
      try {
        if (response.status !== 200) {
          console.error('Error fetching data:', response.statusText);
        } else {
          layout.value = response._data;
        }
      } catch (error) {
        console.error('Error processing data:', error);
      }
    },
  },
);

/* 
const publisherPik: IPublisherDataClientDTO = {
  id: 0,
  x: 0,
  y: 5,
  col_span: 3,
  row_span: 1,
  type_item: 'publisher',
  type_publisher: 'chronicle',
  background_color: null,
  text_color: null,
  border_color: null,
  related_type_publisher: 'special_pik',
  related_publishers: [],
  publisher_news: [],
}; */

const publisherBanner1: IPublisherDataClientDTO = {
  id: 0,
  x: 0,
  y: 5,
  col_span: 3,
  row_span: 1,
  type_item: 'publisher',
  type_publisher: 'chronicle',
  background_color: null,
  text_color: null,
  border_color: null,
  related_type_publisher: 'chronicle_banner1',
  related_publishers: [],
  publisher_news: [],
};

const publisherBanner2: IPublisherDataClientDTO = {
  id: 0,
  x: 0,
  y: 8,
  col_span: 3,
  row_span: 1,
  type_item: 'publisher',
  type_publisher: 'chronicle',
  background_color: null,
  text_color: null,
  border_color: null,
  related_type_publisher: 'chronicle_banner2',
  related_publishers: [],
  publisher_news: [],
};

const publisherBanner3: IPublisherDataClientDTO = {
  id: 0,
  x: 0,
  y: 14,
  col_span: 3,
  row_span: 1,
  type_item: 'publisher',
  type_publisher: 'chronicle',
  background_color: null,
  text_color: null,
  border_color: null,
  related_type_publisher: 'chronicle_banner3',
  related_publishers: [],
  publisher_news: [],
};

const publisherBanner4: IPublisherDataClientDTO = {
  id: 0,
  x: 0,
  y: 19,
  col_span: 3,
  row_span: 1,
  type_item: 'publisher',
  type_publisher: 'chronicle',
  background_color: null,
  text_color: null,
  border_color: null,
  related_type_publisher: 'chronicle_banner4',
  related_publishers: [],
  publisher_news: [],
};

const publisherOneLine: IPublisherDataClientDTO = {
  id: 0,
  x: 0,
  y: 0,
  col_span: 2,
  row_span: 2,
  type_item: 'publisher',
  type_publisher: 'chronicle',
  background_color: null,
  text_color: null,
  border_color: null,
  related_type_publisher: 'one_line',
  related_publishers: [],
  publisher_news: [],
};

const canInsert = (
  y: number,
  item: IPublisherDataClientDTO,
  next?: IPublisherDataClientDTO,
  nextNext?: IPublisherDataClientDTO,
): boolean => {
  if (item.y <= y - 1) return false;

  if (item.col_span === 3) return true;

  if (
    next &&
    item.col_span + (next?.col_span ?? 0) === 3 &&
    item.y === next.y
  ) {
    return true;
  }

  if (
    next &&
    nextNext &&
    item.col_span + (next?.col_span ?? 0) + (nextNext?.col_span ?? 0) === 3 &&
    item.y === next.y &&
    item.y === nextNext.y
  ) {
    return true;
  }

  return false;
};

const getPublicator = (item: IPublisherDataClientDTO) => {
  if (item.related_type_publisher === 'special_project_extra') {
    return isSpecialProjectExtraId(item.text_one_block)
      ? specialProjectExtras[item.text_one_block]
      : undefined;
  }

  return publicators[item.related_type_publisher as keyof typeof publicators];
};

const addedGridData = computed(() => {
  const original = response.data.value || [];
  const result: IPublisherDataClientDTO[] = [];
  // let isInserted = false;
  let isInsertedBanner1 = false;
  let isInsertedBanner2 = false;
  let isInsertedBanner3 = false;
  let isInsertedBanner4 = false;
  let offset = 0; // смещение по y после вставки новых блоков

  result.push(publisherOneLine);

  for (let i = 0; i < original.length; i++) {
    const item = original[i]!;
    const nextItem = i + 1 < original.length ? original[i + 1] : undefined;
    const nextNextItem = i + 2 < original.length ? original[i + 2] : undefined;

    if (item.x > 0) {
      result.push({
        ...item,
        y: item.y + offset,
      });
      continue;
    }

    /* if (!isInserted && canInsert(5, item, nextItem, nextNextItem)) {
      result.push({ ...publisherPik, y: item.y });
      isInserted = true;
      offset++;
    } */

    if (
      !isInsertedBanner1 &&
      canInsert(publisherBanner1.y, item, nextItem, nextNextItem)
    ) {
      result.push({ ...publisherBanner1, y: item.y + offset });
      isInsertedBanner1 = true;
      offset++;
    }

    if (
      !isInsertedBanner2 &&
      canInsert(publisherBanner2.y, item, nextItem, nextNextItem)
    ) {
      result.push({ ...publisherBanner2, y: item.y + offset });
      isInsertedBanner2 = true;
      offset++;
    }

    if (
      !isInsertedBanner3 &&
      canInsert(publisherBanner3.y, item, nextItem, nextNextItem)
    ) {
      result.push({ ...publisherBanner3, y: item.y + offset });
      isInsertedBanner3 = true;
      offset++;
    }

    if (
      !isInsertedBanner4 &&
      canInsert(publisherBanner4.y, item, nextItem, nextNextItem)
    ) {
      result.push({ ...publisherBanner4, y: item.y + offset });
      isInsertedBanner4 = true;
      offset++;
    }

    result.push({
      ...item,
      y: item.y + offset,
    });
  }

  /* if (!isInserted) {
    result.push({ ...publisherPik, y: result[result.length - 1]!.y + 1 });
  } */

  return result;
});

const computedGridData: ComputedRef<TComputedPublisherData[]> = computed(
  (): TComputedPublisherData[] => {
    if ($viewport.isGreaterOrEquals('desktop')) {
      return addedGridData.value!.map((item) => ({
        ...item,
        adjustedColSpan: item.col_span,
      }));
    }

    const result: TComputedPublisherData[] = [];
    let colIndex = 0; // Количество занятых ячеек на строке внутри строки

    for (const item of addedGridData.value!) {
      const originalSpan = item.col_span;
      const span = Math.min(originalSpan, columns.value);
      const remaining = columns.value - colIndex;

      // Если не помещается в строку, растягиваем предыдущий и переходим на новую строку
      if (span > remaining && remaining > 0 && result.length > 0) {
        const prev = result[result.length - 1]!;
        // растягиваем предыдущий элемент на все свободные колонки
        prev.adjustedColSpan = Math.min(
          prev.adjustedColSpan + remaining,
          columns.value,
        );
        colIndex = 0;
      }

      result.push({ ...item, adjustedColSpan: span });
      colIndex = (colIndex + span) % columns.value;
    }

    // Дорастягиваем последний элемент, если остались пустые колонки
    const remainder = columns.value - colIndex;
    if (remainder > 0 && result.length > 0) {
      const last = result[result.length - 1]!;
      last.adjustedColSpan = Math.min(
        last.adjustedColSpan + remainder,
        columns.value,
      );
    }

    return result;
  },
);
</script>

<template>
  <div>
    <MainNews />
    <GridTripple
      v-if="response.status.value === 'success' && layout.length > 0"
      :class="$style.grid"
    >
      <GridItemTripple
        v-for="(item, i) in computedGridData"
        :key="`gridItem${i}`"
        :col-span="item.adjustedColSpan"
        :row-span="item.row_span"
        :col-start="item.x + 1"
        :row-start="item.y + 1"
        :no-border-bottom="item.type_item === 'publisher'"
        :no-border-right="
          item.type_item === 'publisher' || item.type_item === null
        "
      >
        <component
          :is="getPublicator(item)"
          v-if="
            item.type_item === 'publisher' &&
            item.related_type_publisher !== 'special_project_chronicles_year'
          "
          :items="item.related_publishers!.flatMap(getRelatedPublisherItems)"
          :background-color="item.background_color"
          :border-color="item.border_color"
          :text-color="item.text_color"
        />
        <component
          :is="
            publicators[item.related_type_publisher as keyof typeof publicators]
          "
          v-if="
            item.type_item === 'publisher' &&
            item.related_type_publisher === 'special_project_chronicles_year'
          "
          :items="item.related_publishers!.flatMap((i) => (i as unknown) || [])"
          :background-color="item.background_color"
          :border-color="item.border_color"
          :text-color="item.text_color"
          :title-block="item.title_block"
          :font-size-title="item.font_size_title"
          :text-one-block="item.text_one_block"
          :text-two-block="item.text_two_block"
          :image-title-block="item.image_title_block"
          :text-block-button="item.text_block_button"
          :text-two-block-button="item.text_two_block_button"
          :label-color="item.label_color"
          :link-partner="item.link_partner"
          :substrates-color="item.substrates_color"
          :tag-id="item.tag_id"
          :tag="item.tag"
        />
        <component
          :is="publicators['tripple_news']"
          v-if="item.type_item === null"
          :items="item.publisher_news"
        />
        <component
          :is="
            newsTypes[
              item.publisher_news[0].type_item as keyof typeof newsTypes
            ]
          "
          v-if="item.type_item === 'news' && item.publisher_news[0]"
          v-bind="item.publisher_news[0]"
          :class="[
            $style.grid__newsItem,
            {
              [$style.grid__newsItem_noPadding!]:
                item.publisher_news[0].type_item === 'gallery_wide' ||
                item.publisher_news[0].type_item === 'object_images',
            },
          ]"
          :image_one="
            item.publisher_news[0].image_one ||
            item.publisher_news[0].news?.image?.photo ||
            null
          "
          :image_two="
            item.publisher_news[0].image_two ||
            item.publisher_news[0].news?.image?.photo ||
            null
          "
          :image_three="
            item.publisher_news[0].image_three ||
            item.publisher_news[0].news?.image?.photo ||
            null
          "
          :image_four="
            item.publisher_news[0].image_four ||
            item.publisher_news[0].news?.image?.photo ||
            null
          "
          :background-color="item.background_color"
        />
      </GridItemTripple>
    </GridTripple>
    <div
      v-else-if="response.status.value === 'success' && layout.length === 0"
      :class="$style.emptyState"
    >
      <p>Не удалось получить новости</p>
      <p>Попробуйте еще раз</p>
    </div>
    <div
      v-else-if="response.status.value === 'pending'"
      :class="$style.loading"
    >
      <Loader center />
    </div>
    <Subscribe />
    <ChronicleBanner5 />
  </div>
</template>

<style module lang="scss">
.grid {
  overflow: hidden;

  &__newsItem {
    padding: var(--space-2-5x);
    &_noPadding {
      padding: 0;
    }
  }
}

.loading {
  margin-top: 50px;
}

.emptyState {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
}

@media (max-width: 479px) {
  .grid {
    &__newsItem {
      padding: var(--space-2x);
      &_noPadding {
        padding: 0;
      }
    }
  }
}
</style>
